import { DEFAULT_VIEW_SCHEMA_VERSION } from '../../types/views.ts';
import { type AppScenario, section, tab, table, toned } from './build.ts';

const LONG_TEXT = 'Le prospect a décrit un processus d’intégration des nouveaux commerciaux qui repose entièrement sur le responsable d’équipe, sans support écrit, avec des écarts importants entre les recrues selon leur binôme.';

export const contentScenarios: AppScenario[] = [
  {
    id: 'call-analysis',
    title: 'Analyse d’appel (MEDDIC)',
    job: 'Le commercial, après un appel de découverte, sait ce qui manque pour qualifier l’affaire.',
    stresses: ['carte de score à segments', 'faits colorés', 'compte rendu long', 'actions'],
    wished: ['citation de l’appel à côté de chaque critère', 'mise à jour du CRM proposée'],
    payload: {
      status: 'complete',
      tabs: [
        tab('analysis', 'Analyse', [
          section('meddic', 'Qualification MEDDIC', 'summary', 'score_card', {
            title: 'Qualification MEDDIC', subtitle: 'Appel de découverte — Talus Logiciels', status: 'warning', score: 3, score_label: 'critères sur 6',
            facts: [
              { label: 'Metrics', value: 'Temps de montée en compétence : 4 mois', status: 'good' },
              { label: 'Economic buyer', value: 'Non identifié', status: 'critical' },
              { label: 'Decision criteria', value: 'Simplicité, intégration HubSpot', status: 'good' },
              { label: 'Decision process', value: 'Inconnu', status: 'critical' },
              { label: 'Identify pain', value: 'Écarts entre recrues', status: 'good' },
              { label: 'Champion', value: 'Responsable SDR, à confirmer', status: 'warning' },
            ],
            insight: 'L’affaire est réelle mais personne ne signe encore : il faut rencontrer le directeur commercial.',
          }),
          section('next', 'Pour avancer', 'actions', 'action_list', {
            title: 'Pour avancer',
            items: [
              { id: 'n1', label: 'Demander un rendez-vous avec le directeur commercial', priority: 'high', status: 'pending' },
              { id: 'n2', label: 'Envoyer le cas client logistique', priority: 'medium', status: 'pending' },
            ],
          }),
        ]),
        tab('notes', 'Compte rendu', [
          section('report', 'Compte rendu', 'artifact', 'artifact_document', {
            title: 'Compte rendu — Talus Logiciels', subject: 'Appel de découverte du 01/10/2026', body_format: 'markdown',
            body: ['## Contexte', LONG_TEXT, '', '## Ce qu’ils cherchent', '- Un parcours d’intégration écrit', '- Des points de contrôle à 30, 60, 90 jours', '', '## Prochaine étape', 'Rendez-vous avec la direction commerciale avant le 15/10.'].join('\n'),
            meta_items: [{ label: 'Durée de l’appel', value: '42 min' }, { label: 'Participants', value: '3' }],
            export_actions: ['copy_markdown', 'download_markdown'],
          }),
        ]),
      ],
    },
  },
  {
    id: 'competitive-watch',
    title: 'Veille concurrentielle',
    job: 'Le marketing, chaque mois, résume ce qui a changé chez les concurrents.',
    stresses: ['document long avec titres', 'chronologie étroite à côté', 'matrice de comparaison', 'tableau de sources avec liens', 'trois onglets'],
    wished: [],
    payload: {
      status: 'complete',
      tabs: [
        tab('summary', 'Synthèse', [
          section('digest', 'Ce qui a changé en septembre', 'artifact', 'artifact_document', {
            title: 'Veille — septembre 2026', subject: '3 concurrents, 7 annonces', body_format: 'markdown',
            body: ['## L’essentiel', 'Deux concurrents ont baissé leurs prix d’entrée ; un troisième lance un module d’onboarding.', '', '## Par concurrent', '### Ligne Data', '- Nouveau plan à 29 € par utilisateur.', '- ' + LONG_TEXT, '', '### Quartz Conseil', '- Module « onboarding commercial » annoncé, sortie en novembre.', '', '## Ce que ça change pour nous', 'Mettre en avant la mise en route accompagnée, que personne ne propose.'].join('\n'),
            meta_items: [{ label: 'Période', value: 'Septembre 2026' }, { label: 'Sources', value: '12 pages' }],
          }, 'wide'),
          section('announcements', 'Annonces du mois', 'context', 'timeline', {
            title: 'Annonces du mois',
            events: [
              { date: '2026-09-25', label: 'Module onboarding annoncé', kind: 'Quartz Conseil', tone: 'critical', url: 'https://quartz.example/annonce' },
              { date: '2026-09-20', label: 'Partenariat avec un éditeur de CRM', kind: 'Ligne Data', url: 'https://ligne-data.example/blog/partenariat' },
              { date: '2026-09-12', label: 'Nouvelle grille tarifaire à 29 € par utilisateur', kind: 'Ligne Data', tone: 'warning', url: 'https://ligne-data.example/tarifs' },
              { date: '2026-09-03', label: 'Levée de fonds', kind: 'Ponant Services' },
            ],
          }, 'narrow'),
        ]),
        tab('compare', 'Comparatif', [
          section('matrix', 'Nous face aux concurrents', 'detail', 'data_table', table('Nous face aux concurrents', [
            ['criterion', 'Critère'], ['us', 'Nous'], ['ligne', 'Ligne Data'], ['quartz', 'Quartz Conseil'], ['ponant', 'Ponant Services'],
          ], [
            { criterion: 'Mise en route accompagnée', us: toned('Oui', 'good'), ligne: toned('Non', 'critical'), quartz: toned('Partielle', 'warning'), ponant: toned('Non', 'critical') },
            { criterion: 'Onboarding commercial', us: toned('Oui', 'good'), ligne: toned('Non', 'critical'), quartz: toned('Novembre 2026', 'warning'), ponant: toned('Non', 'critical') },
            { criterion: 'Prix d’entrée', us: '39 € / utilisateur', ligne: toned('29 € / utilisateur', 'good'), quartz: '45 € / utilisateur', ponant: null },
            { criterion: 'Intégration CRM', us: toned('HubSpot', 'good'), ligne: toned('Partenariat annoncé', 'warning'), quartz: toned('Non', 'critical'), ponant: toned('Salesforce', 'good') },
            { criterion: 'Support en français', us: toned('Oui', 'good'), ligne: toned('Oui', 'good'), quartz: toned('Oui', 'good'), ponant: toned('Non', 'critical') },
          ], { source_note: 'Pages publiques des concurrents, lues en septembre 2026. Case vide : pas d’information publique.' })),
        ]),
        tab('sources', 'Sources', [
          section('sources', 'Sources lues', 'context', 'data_table', table('Sources lues', [
            ['competitor', 'Concurrent'], ['what', 'Annonce'], ['date', 'Date'], ['url', 'Lien'],
          ], [
            { competitor: 'Ligne Data', what: 'Nouvelle grille tarifaire', date: '12/09/2026', url: 'https://ligne-data.example/tarifs' },
            { competitor: 'Ligne Data', what: 'Partenariat avec un éditeur de CRM', date: '20/09/2026', url: 'https://ligne-data.example/blog/partenariat' },
            { competitor: 'Quartz Conseil', what: 'Module onboarding annoncé', date: '25/09/2026', url: 'https://quartz.example/annonce' },
            { competitor: 'Ponant Services', what: 'Levée de fonds', date: null, url: null },
          ])),
        ]),
      ],
    },
  },
  {
    id: 'edge-cases',
    title: 'Cas limites',
    job: 'Ce que le rendu doit tenir quand les données sont pauvres ou extrêmes.',
    stresses: ['tableau vide avec explication longue', 'une seule ligne', 'titres très longs', 'nombres bruts et booléens', 'section incomplète'],
    wished: ['message vide qui propose une action', 'formats de nombres homogènes'],
    payload: {
      status: 'incomplete',
      issues: ['result/missing:source_missing'],
      tabs: [tab('result', 'Résultat', [
        section('empty', 'Aucune entreprise cette semaine', 'detail', 'data_table', table('Aucune entreprise cette semaine', [['company', 'Entreprise'], ['why', 'Motif']], [], {
          empty_message: 'Les 29 pages lues ne contiennent aucune offre commerciale publiée depuis lundi dans la zone demandée. La méthode a fonctionné (route principale, 29 pages) : il n’y a simplement pas de nouveauté. Relancez lundi prochain ou élargissez la zone à toute la France.',
        })),
        section('single', 'Une très longue étiquette de section qui pourrait déborder sur deux lignes dans la page de résultat', 'detail', 'data_table', table('Une ligne', [['name', 'Nom'], ['count', 'Nombre'], ['active', 'Actif'], ['amount', 'Montant brut']], [
          { name: 'Archipel Conseil', count: 1234567, active: true, amount: 98765.4321 },
        ])),
        section('gaps', 'Courbe avec trous et valeurs négatives', 'detail', 'chart', {
          kind: 'line', unit: '€',
          categories: Array.from({ length: 52 }, (_, index) => `S${index + 1}`),
          series: [{ key: 'margin', label: 'Marge hebdomadaire', values: Array.from({ length: 52 }, (_, index) => (index % 9 === 4 ? null : Math.round(Math.sin(index / 5) * 12000 + index * 150))) }],
        }),
        section('empty-chart', 'Graphique sans données', 'detail', 'chart', {
          kind: 'bar', categories: ['A', 'B'], series: [{ key: 'x', label: 'Valeur', values: [null, null] }],
          empty_message: 'Aucune valeur cette semaine : les sources lues ne donnent pas de chiffre.',
        }),
        section('raw-metrics', 'Chiffres bruts', 'summary', 'metric_grid', {
          metrics: [{ label: 'Montant', value: 1234567.891 }, { label: 'Ratio', value: 0.3333333 }, { label: 'Texte', value: 'non disponible' }],
        }),
        // What the server writes when a required section's source is missing.
        {
          id: 'missing', label: 'Section dont la source manque', role: 'detail', viewType: 'data_table',
          envelope: { view_type: 'fallback_panel', schema_version: DEFAULT_VIEW_SCHEMA_VERSION, data: {
            title: 'Section dont la source manque', reason: 'source_no_data',
            message: 'Les données attendues pour cette partie manquent ou sont incomplètes. Vérifiez la source puis relancez le test.',
          } },
        },
      ])],
    },
  },
];
